import { PanelStatus } from '@components/quotePanel';
import { ToastType } from '@components/toast';
import { QuoteItemsAttributesPatchRequestBody } from '@lio/api-types';
import { QuoteItemAttribute } from '@models/quoteItem';
import { getUnsavedAttributeValue } from '@pages/QuoteDetails/helpers/getUnsavedAttributeValue';
import { useUpdateQuoteItemAttributes } from '@services/quoteItem';
import { PanelInstance } from '@store/Quote/Quote.types';
import { useQueryClient } from '@tanstack/react-query';
import { setDateToMidnight } from '@utils/date';
import { pick } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useCurrentQuoteStoreState } from '@store/Quote/QuoteStore';

export const useSaveChanges = (quoteId: string, panelInstances: PanelInstance[]) => {
  const updateAttributesMutation = useUpdateQuoteItemAttributes();
  const userAuth = useStore((state) => state.userAuth, shallow);
  const { isCustomerUser } = userAuth;
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const quoteStoreContext = useCurrentQuoteStoreState();

  const saveChanges = useCallback(async () => {
    const { panelInstanceStatusById } = quoteStoreContext;
    const { attributesById, clearAttributeValues } = quoteStoreContext;

    const statusValues = Object.values(panelInstanceStatusById);
    const areInvalidFields = statusValues.some((value) => value === PanelStatus.Unfinished);
    let isBusinessClassUpdate = false;
    let isNamedInsuredNameUpdate = false;
    const requestArray: { id: string; quoteItemAttributes: QuoteItemAttribute[] }[] = [];

    panelInstances.forEach((panelInstance) => {
      const { quoteItem, panel } = panelInstance;

      const fieldsInPanel = () => {
        const panelSectionIds = panel.panelSections.map((s) => s.id);
        return Object.values(quoteItem.attributeTypes).filter((at) =>
          panelSectionIds.includes(at.panelSectionId as string),
        );
      };
      const fieldRefs = fieldsInPanel().map((f) => f.attribute?.id ?? `${quoteItem.id}-${f.id}`);
      const storedAttributes = pick(attributesById, fieldRefs);

      const unsavedFieldsInPanel = fieldsInPanel().filter(
        (field) =>
          getUnsavedAttributeValue(storedAttributes, field, quoteItem.id, 'value') !== undefined ||
          getUnsavedAttributeValue(storedAttributes, field, quoteItem.id, 'valueOverride') !== undefined,
      );

      if (unsavedFieldsInPanel.length) {
        isBusinessClassUpdate = unsavedFieldsInPanel.some((el) => el.codeName === 'BusinessClass');
        isNamedInsuredNameUpdate = unsavedFieldsInPanel.some((el) => el.codeName === 'CustomerName');
        if (requestArray.some((el) => el.id === quoteItem.id)) {
          const index = requestArray.findIndex((el) => el.id === quoteItem.id);
          requestArray[index].quoteItemAttributes = requestArray[index].quoteItemAttributes.concat(
            unsavedFieldsInPanel.map((f) => {
              const value = getUnsavedAttributeValue(storedAttributes, f, quoteItem.id, 'value');
              const valueOverride = getUnsavedAttributeValue(storedAttributes, f, quoteItem.id, 'valueOverride');
              const isUpdatedByUser = getUnsavedAttributeValue(storedAttributes, f, quoteItem.id, 'isUpdatedByUser');

              return {
                value: value && f.dataType === 'DateTime' ? setDateToMidnight(value as unknown as Date) : value,
                valueOverride:
                  valueOverride && f.dataType === 'DateTime'
                    ? setDateToMidnight(valueOverride as unknown as Date)
                    : valueOverride,
                id: f.attribute?.id,
                quoteItemAttributeTypeId: f.id,
                isUpdatedByUser,
              } as QuoteItemAttribute;
            }),
          );
        } else {
          requestArray.push({
            id: quoteItem.id,
            quoteItemAttributes: unsavedFieldsInPanel.map((f) => {
              const value = getUnsavedAttributeValue(storedAttributes, f, quoteItem.id, 'value');
              const valueOverride = getUnsavedAttributeValue(storedAttributes, f, quoteItem.id, 'valueOverride');
              const isUpdatedByUser = getUnsavedAttributeValue(storedAttributes, f, quoteItem.id, 'isUpdatedByUser');
              return {
                value: value && f.dataType === 'DateTime' ? setDateToMidnight(value as unknown as Date) : value,
                valueOverride:
                  valueOverride && f.dataType === 'DateTime'
                    ? setDateToMidnight(valueOverride as unknown as Date)
                    : valueOverride,
                id: f.attribute?.id,
                quoteItemAttributeTypeId: f.id,
                isUpdatedByUser,
              } as QuoteItemAttribute;
            }),
          });
        }
      }
    });

    if (requestArray !== undefined && requestArray.length > 0) {
      await updateAttributesMutation
        .mutateAsync({
          quoteId,
          quoteItems: requestArray as QuoteItemsAttributesPatchRequestBody['quoteItems'],
        })
        .then(() => {
          if (areInvalidFields && !isCustomerUser) {
            enqueueSnackbar('Details saved with validation errors', {
              variant: ToastType.Info,
            });
          } else {
            enqueueSnackbar('Details saved successfully', {
              variant: ToastType.Success,
            });
          }
          if (isBusinessClassUpdate || isNamedInsuredNameUpdate) {
            queryClient.invalidateQueries({
              queryKey: ['quote', { quoteId }],
            });
          }
          if (isNamedInsuredNameUpdate) {
            queryClient.invalidateQueries({
              queryKey: ['customer'],
            });
          }
          clearAttributeValues(Object.keys(attributesById));
        })
        .catch((e) => {
          enqueueSnackbar(e?.message ?? 'Details were not saved', {
            variant: ToastType.Error,
            persist: true,
          });
        });
    }
  }, [
    enqueueSnackbar,
    isCustomerUser,
    panelInstances,
    queryClient,
    quoteId,
    quoteStoreContext,
    updateAttributesMutation,
  ]);

  return { saveChanges, isLoading: updateAttributesMutation.isPending, status: updateAttributesMutation.status };
};
